import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  TweaksPanel,
  TweakRadio,
  TweakSection,
  TweakToggle,
  useTweaks,
} from './XlabsTweaks';
import InternalBackLink from '../Internal/InternalBackLink';
import useInternalTheme from '../../hooks/useInternalTheme';
import { writeTheme } from '../../utils/internalTheme';
import './XlabsHub.css';

/**
 * xlabs configurator hub — ported from design/xlabs-configurator (2)/index.html
 * See docs/XLABS_HUB.md
 */

const TWEAK_DEFAULTS = {
  navMode: 'grid',
  density: 'compact',
  showMeta: false,
};

/** Stage cards — app routes or `#` until ported (docs/XLABS_HUB.md). */
const ROUTES = [
  {
    num: '01',
    letter: 'L',
    key: 'locate',
    href: '/xlabs/locate',
    name: 'Locate',
    sub: 'Plot polygon · setbacks · north heading',
    meta: ['configurator', 'R3F'],
    cmd: 'xlabs locate',
    arg: '--plot ./site.geojson',
    desc: 'open the site plot editor',
  },
  {
    num: '02',
    letter: 'D',
    key: 'design',
    href: '/xlabs/design',
    name: 'Design',
    sub: 'Place catalog ADUs on the lot · stack · gizmo move',
    meta: ['configurator', 'R3F Place'],
    cmd: 'xlabs design',
    arg: '--catalog ./models',
    desc: 'place Construx / SoftPlan structures on the lot',
  },
  {
    num: '03',
    letter: 'A',
    key: 'analyse',
    href: '#analyse',
    name: 'Analyse',
    sub: '3D heat-map · cost · carbon · structural',
    meta: ['configurator', 'three.js'],
    cmd: 'xlabs analyse',
    arg: '--layers cost,carbon',
    desc: '3D analysis with heat-mapped layers',
  },
  {
    num: '04',
    letter: 'B',
    key: 'build',
    href: '#build',
    name: 'Build',
    sub: 'CLT panel nesting · DXF / IFC / BoM exports',
    meta: ['configurator', 'exports'],
    cmd: 'xlabs build',
    arg: '--out exports/',
    desc: 'CLT nesting + export jobs',
  },
  {
    num: '05',
    letter: 'C',
    key: 'checkout',
    href: '#checkout',
    name: 'Checkout',
    sub: 'Stripe full · 20% deposit · Katapult financing',
    meta: ['PRD 01', 'Stripe'],
    cmd: 'xlabs portal',
    arg: 'checkout --order X-024A',
    desc: 'customer checkout flow',
  },
  {
    num: '06',
    letter: 'P',
    key: 'portal',
    href: '#portal',
    name: 'Portal',
    sub: 'Post-purchase · timeline · payments · documents · scheduling',
    meta: ['PRD 01', 'customer'],
    cmd: 'xlabs portal',
    arg: 'dashboard --order X-024A',
    desc: 'post-purchase customer dashboard',
  },
  {
    num: '07',
    letter: 'O',
    key: 'ops',
    href: '#ops',
    name: 'Ops',
    sub: 'Board · 10 stages · gates · checklists · supplier dispatch',
    meta: ['PRD 03', 'Supabase'],
    cmd: 'xlabs ops',
    arg: 'board',
    desc: 'ops console · all orders',
  },
];

const SUBROUTES = [
  {
    parent: 'design',
    letter: 'X',
    name: 'Exterior',
    href: '#exterior',
    sub: 'Cladding · roof · trim · windows · doors — itemized spec sheet',
  },
];

const FOOT_LINKS = [
  { label: 'architecture →', to: '/xlabs' },
  { label: 'prds →', href: '#prds' },
  { label: 'report →', href: '#report' },
  { label: 'port →', href: '#port' },
  { label: 'cursor →', href: '#cursor' },
  { label: 'fixes →', href: '#fixes' },
  { label: 'pass 1 →', href: '#pass-1' },
  { label: 'diff →', href: '#diff' },
  { label: 'pass 2 →', href: '#pass-2' },
  { label: 'pass 3 →', href: '#pass-3' },
  { label: 'pass 4 →', href: '#pass-4' },
];

function HubLink({ href, className, children, ...rest }) {
  if (href.startsWith('/')) {
    return (
      <Link to={href} className={className} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} {...rest}>
      {children}
    </a>
  );
}

function GridNav({ showMeta }) {
  return (
    <div className="nav-grid">
      {ROUTES.map((r) => (
        <HubLink key={r.key} className="card" href={r.href}>
          <span className="num">
            {r.num} · {r.letter}
          </span>
          <span className="name">{r.name}</span>
          <span className="sub">{r.sub}</span>
          {showMeta && (
            <span className="meta">
              {r.meta.map((m) => (
                <span key={m}>{m}</span>
              ))}
            </span>
          )}
        </HubLink>
      ))}
    </div>
  );
}

function TimelineNav({ showMeta }) {
  return (
    <div className="nav-timeline">
      <div className="rail" />
      {ROUTES.map((r) => (
        <HubLink key={r.key} className="stop" href={r.href}>
          <div>
            <div className="dot" />
          </div>
          <div className="body">
            <div className="row1">
              <span className="num">
                {r.num} · {r.letter}
              </span>
              <span className="name">{r.name}</span>
            </div>
            <div className="sub">{r.sub}</div>
            {showMeta && (
              <div
                className="sub"
                style={{
                  marginTop: 6,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  color: 'var(--mid)',
                }}
              >
                {r.meta.join(' · ')}
              </div>
            )}
          </div>
        </HubLink>
      ))}
    </div>
  );
}

function goHref(navigate, href) {
  if (href.startsWith('/')) navigate(href);
  else window.location.hash = href.replace(/^#/, '');
}

function CliNav() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const ALL = [
    ...ROUTES,
    ...SUBROUTES.map((s) => {
      const parent = ROUTES.find((r) => r.key === s.parent);
      return {
        ...s,
        key: `sub-${s.parent}-${s.name.toLowerCase()}`,
        num: `${parent?.num}·`,
        cmd: `xlabs ${s.parent} exterior`,
        arg: '--spec',
        desc: s.sub,
      };
    }),
  ];

  const filtered = ALL.filter(
    (r) =>
      !query ||
      r.name.toLowerCase().includes(query.toLowerCase()) ||
      (r.cmd || '').includes(query.toLowerCase()) ||
      (r.key || '').includes(query.toLowerCase()),
  );

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[focusIdx]) goHref(navigate, filtered[focusIdx].href);
    } else if (e.key === 'Escape') {
      setQuery('');
      setFocusIdx(0);
    }
  };

  return (
    <div className="nav-cli">
      <div className="heading"># xlabs configurator · type stage name or use ↑↓ + ⏎</div>
      {filtered.map((r, i) => (
        <HubLink
          key={r.key}
          className={`cmd${i === focusIdx ? ' focus' : ''}`}
          href={r.href}
          onMouseEnter={() => setFocusIdx(i)}
        >
          <span className="prompt">$ </span>
          <span className="k">{r.cmd}</span>{' '}
          <span className="arg">{r.arg}</span>
          <span className="desc"># {r.desc}</span>
        </HubLink>
      ))}
      {filtered.length === 0 && (
        <div style={{ color: 'var(--mid)', padding: '4px 0' }}>
          <span className="prompt">$ </span>no match. press esc to clear.
        </div>
      )}
      <div className="input">
        <span className="prompt">$</span>
        <input
          ref={inputRef}
          value={query}
          placeholder="locate, design, analyse, build…"
          onChange={(e) => {
            setQuery(e.target.value);
            setFocusIdx(0);
          }}
          onKeyDown={onKey}
        />
        <span className="blink" />
      </div>
    </div>
  );
}

function CarouselNav({ showMeta }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const r = ROUTES[idx];
  const prev = () => setIdx((i) => (i - 1 + ROUTES.length) % ROUTES.length);
  const next = () => setIdx((i) => (i + 1) % ROUTES.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Enter') goHref(navigate, r.href);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [r.href, navigate]);

  return (
    <div className="nav-carousel">
      <div key={idx} className="stage stage-anim">
        <div className="num">
          stage {r.num} · {r.letter}
        </div>
        <div className="name">{r.name}.</div>
        <div className="sub">{r.sub}</div>
        {showMeta && (
          <div
            className="sub"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--mid)',
              marginBottom: 24,
              marginTop: -8,
            }}
          >
            {r.meta.join(' · ')}
          </div>
        )}
        <HubLink className="enter" href={r.href}>
          enter →
        </HubLink>
      </div>
      <div className="arrows">
        <button type="button" className="arrow" onClick={prev} aria-label="prev">
          ←
        </button>
        <div className="dots">
          {ROUTES.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`dot${i === idx ? ' on' : ''}`}
              onClick={() => setIdx(i)}
              aria-label={`go to ${i + 1}`}
            />
          ))}
        </div>
        <button type="button" className="arrow" onClick={next} aria-label="next">
          →
        </button>
      </div>
    </div>
  );
}

const NAV_BY_MODE = {
  grid: GridNav,
  timeline: TimelineNav,
  cli: CliNav,
  carousel: CarouselNav,
};

export default function XlabsHub() {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const theme = useInternalTheme();
  const onTheme = useCallback((v) => writeTheme(v), []);
  const Nav = NAV_BY_MODE[t.navMode] || GridNav;

  return (
    <div
      className={`xlabs-hub density-${t.density}`}
      data-hub-theme={theme === 'light' ? undefined : theme}
    >
      <Helmet>
        <title>xlabs · configurator</title>
        <meta
          name="description"
          content="xlabs configurator hub — Locate, Design, Analyse, Build, Checkout, Portal, Ops."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="hub">
        <div className="hub-card">
          <div className="hub-h hub-h-row">
            <span>xlabs · configurator v2</span>
            <InternalBackLink className="hub-back" />
          </div>
          <h1 className="hub-title">Sentinel Way · Cabin 02</h1>
          <p className="hub-sub">
            Four-stage parametric configurator. Real Redux store, optimistic patches, Speckle
            round-trip simulation, undo/redo, keyboard shortcuts. Use the bottom-right control to
            switch how you navigate.
          </p>

          <Nav showMeta={t.showMeta} />

          <div className="subroutes">
            <div className="h">Deep links</div>
            <div className="row">
              {SUBROUTES.map((s) => {
                const parent = ROUTES.find((r) => r.key === s.parent);
                return (
                  <a key={s.href} href={s.href}>
                    <span className="parent">
                      {parent?.num} {parent?.name}
                    </span>
                    <span className="arrow">›</span>
                    <span className="nm">{s.name}</span>
                    {t.showMeta && <span className="desc">— {s.sub}</span>}
                  </a>
                );
              })}
            </div>
          </div>

          <div className="hub-foot">
            <div className="hub-foot-status">
              <span className="pip" />
              <span>store ready</span>
            </div>
            <nav className="hub-foot-links" aria-label="Hub docs">
              {FOOT_LINKS.map((item) =>
                item.to ? (
                  <Link key={item.label} to={item.to}>
                    {item.label}
                  </Link>
                ) : (
                  <a key={item.label} href={item.href}>
                    {item.label}
                  </a>
                ),
              )}
            </nav>
          </div>
        </div>

        <TweaksPanel title="View" defaultOpen={false}>
          <TweakSection label="Navigation" />
          <TweakRadio
            label="Mode"
            value={t.navMode}
            options={[
              { value: 'grid', label: 'Grid' },
              { value: 'timeline', label: 'Line' },
              { value: 'cli', label: 'CLI' },
              { value: 'carousel', label: 'One' },
            ]}
            onChange={(v) => setTweak('navMode', v)}
          />
          <TweakRadio
            label="Density"
            value={t.density}
            options={['compact', 'regular', 'spacious']}
            onChange={(v) => setTweak('density', v)}
          />
          <TweakToggle
            label="Show meta tags"
            value={t.showMeta}
            onChange={(v) => setTweak('showMeta', v)}
          />
          <TweakSection label="Theme" />
          <TweakRadio
            label="Skin"
            value={theme}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'paper', label: 'Paper' },
              { value: 'blueprint', label: 'Blue' },
              { value: 'terminal', label: 'Term' },
            ]}
            onChange={onTheme}
          />
        </TweaksPanel>
      </div>
    </div>
  );
}
